import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import Loading from '../components/Loading.jsx';
import Button from '../components/ui/Button.jsx';
import Input from '../components/ui/Input.jsx';
import Textarea from '../components/ui/Textarea.jsx';
import { GoldFrame } from '../components/decor';
import { InvitationArtwork } from '../components/decor';
import Divider from '../components/decor/Divider.jsx';
import { ApiError } from '../lib/api.js';
import { rsvpSchema } from '../lib/schemas/rsvp.js';
import { EVENT_DATA } from '../lib/event.js';
import {
  getConfirmedInvitationCutoff,
  isRsvpClosed,
} from '../lib/rsvpDeadline.js';
import { useConfirmacion, getConfirmacionStatus } from '../hooks/useConfirmacion.js';
import { useEventSettings } from '../hooks/useEventSettings.js';

// Public RSVP page (FR-2).
// Renders inside the shared GoldFrame so the guest sees the wedding
// invitation artwork, then their personalised state below:
//   404 → Invitación no encontrada
//   423 → backend message verbatim + disabled form
//   success → ¡Gracias, {nombre}!
//   form → greeting + radio Sí/No + acompanantes + canción opcional
export default function Confirmacion() {
  const { token = '' } = useParams();
  const { query, mutation } = useConfirmacion(token);
  const { data: eventSettings } = useEventSettings();
  const status = getConfirmacionStatus(query);
  const invitacion = query.data;
  const hasAcknowledgedAnswer =
    mutation.isSuccess && typeof mutation.variables?.asiste === 'boolean';
  const isAccepted = hasAcknowledgedAnswer
    ? mutation.variables.asiste
    : invitacion?.confirmado === 1;
  const [clockRevision, setClockRevision] = useState(0);
  const confirmedInvitationCutoff = isAccepted
    ? getConfirmedInvitationCutoff(eventSettings?.event_date)
    : null;
  const confirmedInvitationCutoffMs = confirmedInvitationCutoff?.getTime() ?? null;
  const confirmedInvitationWindowActive =
    isAccepted && (confirmedInvitationCutoffMs === null || Date.now() < confirmedInvitationCutoffMs);

  useEffect(() => {
    if (!isAccepted || confirmedInvitationCutoffMs === null) return undefined;

    const remainingMs = confirmedInvitationCutoffMs - Date.now();
    if (remainingMs <= 0) return undefined;

    // Recheck at least daily so long windows fit in browser timer limits and
    // a clock adjustment cannot keep the invitation visible past its cutoff.
    const timeoutId = setTimeout(
      () => setClockRevision((revision) => revision + 1),
      Math.min(remainingMs, 24 * 60 * 60 * 1000),
    );
    return () => clearTimeout(timeoutId);
  }, [isAccepted, confirmedInvitationCutoffMs, clockRevision]);

  // Settings are the only deadline source for the client gate. While the
  // request is unresolved or failed, keep gifts visible unless the API
  // reports that this invitation is locked.
  const rsvpDeadlinePassed = isRsvpClosed(eventSettings);
  const hideRsvpPrompt = rsvpDeadlinePassed || Boolean(invitacion?.locked) || mutation.isSuccess;

  const error = query.error;

  // Accepted guests use the admin wedding-date window. Every other status
  // keeps the existing client deadline and server lock gift visibility rules.
  const giftsHidden = isAccepted
    ? !confirmedInvitationWindowActive
    : rsvpDeadlinePassed || Boolean(invitacion?.locked);

  if (status === 'loading') return <Loading label="Cargando invitación..." />;

  if (status === 'not-found') {
    return <NotFoundState />;
  }

  if (status === 'locked') {
    return <LockedState message={error?.message} eventSettings={eventSettings} />;
  }

  if (status === 'error') {
    return <ErrorState message={error?.message} eventSettings={eventSettings} />;
  }

  // Lock deadline passed: render the success screen ("Gracias.") instead of
  // the editable form. Submission is also blocked server-side via the
  // existing isRsvpLocked() check in POST, but this keeps guests from
  // even seeing the "Enviar confirmación" button.
  if (invitacion?.locked) {
    return (
      <SuccessState
        invitacion={invitacion}
        eventSettings={eventSettings}
        giftsHidden={giftsHidden}
        isAccepted={isAccepted}
        confirmedInvitationWindowActive={confirmedInvitationWindowActive}
        hideRsvpPrompt={hideRsvpPrompt}
      />
    );
  }

  return (
    <ConfirmacionForm
      invitacion={invitacion}
      mutation={mutation}
      eventSettings={eventSettings}
      giftsHidden={giftsHidden}
      isAccepted={isAccepted}
      confirmedInvitationWindowActive={confirmedInvitationWindowActive}
      hideRsvpPrompt={hideRsvpPrompt}
    />
  );
}

function NotFoundState() {
  return (
    <GoldFrame>
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-8 text-center">
        <h1 className="font-heading text-3xl text-ink">Invitaci&oacute;n no encontrada</h1>
        <p className="text-base text-ink/70">
          El enlace que usaste no corresponde a ninguna invitaci&oacute;n. Verific&aacute; que
          est&eacute; bien copiado.
        </p>
      </div>
    </GoldFrame>
  );
}

function LockedState({ message, eventSettings }) {
  const text =
    message ??
    'El plazo de confirmación cerró. Si necesitás un cambio, contactate con los novios.';

  useEffect(() => {
    toast.error(text);
  }, [text]);

  return (
    <InvitationArtwork eventSettings={eventSettings}>
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-6 text-center">
        <h2 className="font-heading text-2xl text-ink">Confirmaci&oacute;n cerrada</h2>
        <p
          className="rounded-md border border-error bg-creamTop/40 p-4 text-sm text-error"
          role="alert"
          data-testid="locked-message"
        >
          {text}
        </p>
        <p className="text-sm text-ink/70">
          Si necesit&aacute;s un cambio, contactate directamente con los novios.
        </p>
      </div>
    </InvitationArtwork>
  );
}

function ErrorState({ message, eventSettings }) {
  return (
    <InvitationArtwork eventSettings={eventSettings}>
      <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-6 text-center">
        <h2 className="font-heading text-2xl text-ink">Algo sali&oacute; mal</h2>
        <p className="text-sm text-ink/70">
          {message ?? 'No pudimos cargar tu invitaci&oacute;n. Intent&aacute; de nuevo en unos minutos.'}
        </p>
      </div>
    </InvitationArtwork>
  );
}

function ConfirmacionForm({
  invitacion,
  mutation,
  eventSettings,
  giftsHidden,
  isAccepted,
  confirmedInvitationWindowActive,
  hideRsvpPrompt,
}) {
  const yaRespondio = invitacion.confirmado === 1 || invitacion.confirmado === -1;
  const maxAcompanantes = Number(invitacion.acompanantes_max ?? 1);

  const defaults = useMemo(
    () => ({
      asiste: invitacion.confirmado === 1 ? 'si' : invitacion.confirmado === -1 ? 'no' : '',
      acompanantes_confirmados:
        invitacion.acompanantes_confirmados != null
          ? Number(invitacion.acompanantes_confirmados)
          : 1,
      cancion_sugerida: invitacion.cancion_sugerida ?? '',
    }),
    [invitacion],
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(rsvpSchema),
    defaultValues: defaults,
    mode: 'onTouched',
  });

  const asisteValue = watch('asiste');
  // Hide the companions input entirely for solo entries (max < 2): with
  // only one slot there is nothing to count — the answer is always 1.
  const showAcompanantes = asisteValue === 'si' && maxAcompanantes >= 2;

  const onSubmit = async (values) => {
    const payload = {
      asiste: values.asiste === 'si',
      acompanantes_confirmados: values.asiste === 'si' ? Number(values.acompanantes_confirmados) : 0,
    };
    if (values.cancion_sugerida && values.cancion_sugerida.trim()) {
      payload.cancion_sugerida = values.cancion_sugerida;
    }

    try {
      await mutation.mutateAsync(payload);
      toast.success('¡Gracias! Tu confirmación quedó registrada.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        toast.error(err.message);
        return;
      }
      toast.error(err?.message ?? 'No pudimos registrar tu confirmación. Intentá de nuevo.');
    }
  };

  if (mutation.isSuccess) {
    return (
      <SuccessState
        invitacion={invitacion}
        eventSettings={eventSettings}
        giftsHidden={giftsHidden}
        isAccepted={isAccepted}
        confirmedInvitationWindowActive={confirmedInvitationWindowActive}
        hideRsvpPrompt={hideRsvpPrompt}
      />
    );
  }

  return (
    <InvitationArtwork
      eventSettings={eventSettings}
      confirmedInvitationWindowActive={isAccepted ? confirmedInvitationWindowActive : undefined}
      hideRsvpPrompt={hideRsvpPrompt}
      preBlessing={
        <div
          className="font-script text-[40px] leading-tight text-ink sm:text-5xl"
          style={{ fontFamily: "'Great Vibes', 'Allura', cursive" }}
        >
          <h2 className="leading-tight">Hola,</h2>
          <p className="-mt-1 leading-tight">{invitacion.nombre}</p>
        </div>
      }
    >
      <div className="mx-auto flex max-w-md flex-col gap-5 py-6 text-center">
        <p className="text-sm text-ink/70">
          {yaRespondio
            ? 'Ya respondiste esta invitación. Puedes actualizar tu respuesta.'
            : 'Confirmá tu asistencia para nuestra boda.'}
        </p>

        <CompanionsBanner
          max={maxAcompanantes}
          yaConfirmoAsistencia={invitacion.confirmado === 1}
          yaRechazo={invitacion.confirmado === -1}
        />

        {yaRespondio && (
          <p className="text-sm text-ink/70">Ya confirmaste.</p>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="mt-2 flex flex-col gap-4 text-left">
          <fieldset className="flex flex-col gap-2 text-ink">
            <legend className="text-sm font-medium">¿Asistirás a la boda?</legend>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" value="si" {...register('asiste')} className="accent-gold-deep" />
              <span>Sí, asisto</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" value="no" {...register('asiste')} className="accent-gold-deep" />
              <span>No puedo asistir</span>
            </label>
            {errors.asiste && (
              <span className="text-xs text-error" role="alert">
                {errors.asiste.message}
              </span>
            )}
          </fieldset>

          {showAcompanantes && (
            <Input
              label={`¿Cuántas personas asistirán? (máximo ${maxAcompanantes})`}
              type="number"
              min={1}
              max={maxAcompanantes}
              step={1}
              {...register('acompanantes_confirmados', {
                setValueAs: (v) => {
                  if (v === '' || v === null || v === undefined) return 1;
                  const n = Number(v);
                  if (Number.isNaN(n)) return 1;
                  return Math.max(1, Math.floor(n));
                },
                validate: (value) => {
                  const n = Number(value);
                  if (n > maxAcompanantes) {
                    return maxAcompanantes === 1
                      ? 'Tu invitación incluye 1 persona'
                      : `Tu invitación permite hasta ${maxAcompanantes} personas`;
                  }
                  return true;
                },
              })}
              error={errors.acompanantes_confirmados?.message}
            />
          )}

          <Textarea
            label="¿Qué canción no puede faltar este día? (opcional)"
            rows={2}
            maxLength={500}
            placeholder="Tu tema favorito para la fiesta..."
            {...register('cancion_sugerida')}
            error={errors.cancion_sugerida?.message}
          />

          {mutation.isError && mutation.error instanceof ApiError && (
            <p className="text-sm text-error" role="alert">
              {mutation.error.message}
            </p>
          )}

          <Button type="submit" disabled={isSubmitting || mutation.isLoading}>
            {mutation.isLoading || isSubmitting ? 'Enviando...' : 'Enviar confirmación'}
          </Button>

          {!giftsHidden && <GiftsSection />}
        </form>
      </div>
    </InvitationArtwork>
  );
}

function SuccessState({
  invitacion,
  eventSettings,
  giftsHidden,
  isAccepted,
  confirmedInvitationWindowActive,
  hideRsvpPrompt,
}) {
  // After the RSVP lock, show its contextual copy unless an accepted guest's
  // two-week invitation window has expired; that state keeps only a neutral
  // thank-you and the couple signature.
  const showLockedMessage = Boolean(invitacion?.locked);
  const confirmedInvitationExpired = isAccepted && !confirmedInvitationWindowActive;
  const lockedCopy = showLockedMessage && !confirmedInvitationExpired
    ? invitacion?.confirmado === 1
      ? EVENT_DATA.lockedMessages.confirmed
      : invitacion?.confirmado === -1
        ? EVENT_DATA.lockedMessages.declined
        : EVENT_DATA.lockedMessages.notResponded
    : null;
  const lockedParagraphs = lockedCopy
    ? lockedCopy.includes('LA LISTA SE HA CERRADO')
      ? lockedCopy
          .split(/\s*(LA LISTA SE HA CERRADO)\s*/)
          .map((paragraph) => paragraph.trim())
          .filter(Boolean)
      : [lockedCopy]
    : [];

  return (
    <InvitationArtwork
      eventSettings={eventSettings}
      confirmedInvitationWindowActive={isAccepted ? confirmedInvitationWindowActive : undefined}
      hideRsvpPrompt={hideRsvpPrompt}
      preBlessing={
        <div
          className="font-script text-[40px] leading-tight text-ink sm:text-5xl"
          style={{ fontFamily: "'Great Vibes', 'Allura', cursive" }}
        >
          <h2 className="leading-tight">Hola,</h2>
          <p className="-mt-1 leading-tight">{invitacion.nombre}</p>
        </div>
      }
    >
      <div className={`mx-auto flex max-w-md flex-col items-center gap-3 text-center ${lockedCopy ? 'py-6' : 'py-8'}`}>
        {lockedCopy && (
          <div
            className="flex flex-col gap-4 text-sm leading-relaxed text-ink/80"
            role="alert"
            data-testid="locked-message"
          >
            {lockedParagraphs.map((paragraph, index) => (
              <p key={`${index}-${paragraph}`}>{paragraph}</p>
            ))}
          </div>
        )}
        {(lockedCopy || confirmedInvitationExpired) && (
          <p className="font-script text-xl leading-relaxed text-gold-deep sm:text-2xl">
            {EVENT_DATA.couple.display}
          </p>
        )}
        <h2
          className="break-words font-script text-[32px] leading-tight text-gold-deep sm:text-5xl"
          style={{ fontFamily: "'Great Vibes', 'Allura', cursive" }}
        >
          Gracias.
        </h2>
        {!giftsHidden && <GiftsSection />}
      </div>
    </InvitationArtwork>
  );
}

// Regalos (gift info) is shown both inside the RSVP form (so guests see it
// while confirming) AND on the success page (so the thank-you screen
// doesn't lose the bank details). Extracted here to keep the copy in
// one place — both surfaces pull the same section, and both drop it
// once the guest list is closed (the RSVP deadline has passed, or the API
// answered `locked: true`), so the invitation no longer asks for
// contributions.
function GiftsSection() {
  return (
    <section
      className="mt-6 w-full border-t border-gold/30 pt-4 text-left"
      data-testid="gifts-section"
    >
      <svg
        viewBox="0 0 32 32"
        className="mx-auto mb-2 h-8 w-8 text-gold-deep"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        data-testid="gifts-icon"
      >
        <rect x="5" y="14" width="22" height="14" rx="1" />
        <path d="M16 14v14" />
        <path d="M5 19h22" />
        <path d="M16 14c-2-4-7-4-7-1 0 2 3 2 7 1" />
        <path d="M16 14c2-4 7-4 7-1 0 2-3 2-7 1" />
      </svg>
      <h3 className="text-center font-heading text-lg text-ink">{EVENT_DATA.gifts.title}</h3>
      <p className="mt-1 text-center text-sm text-ink/70">{EVENT_DATA.gifts.intro}</p>
      <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm text-ink">
        <dt className="font-medium text-text-muted">Banco</dt>
        <dd>{EVENT_DATA.gifts.bank}</dd>
        <dt className="font-medium text-text-muted">Tipo de cuenta</dt>
        <dd>{EVENT_DATA.gifts.accountType}</dd>
        <dt className="font-medium text-text-muted">Titular</dt>
        <dd>{EVENT_DATA.gifts.holder}</dd>
        <dt className="font-medium text-text-muted">CI</dt>
        <dd className="flex items-center gap-2">
          <span>{EVENT_DATA.gifts.ci}</span>
          <CopyButton value={EVENT_DATA.gifts.ci} label="Copiar CI" />
        </dd>
        <dt className="font-medium text-text-muted">#cta</dt>
        <dd className="flex items-center gap-2">
          <span className="select-all font-mono">{EVENT_DATA.gifts.cta}</span>
          <CopyButton value={EVENT_DATA.gifts.cta} label="Copiar #cta" />
        </dd>
      </dl>
    </section>
  );
}

// Small inline button used next to the bank-detail values (CI / #cta)
// so the guest can copy them to the bank app with one click. Icon-only
// (two overlapping rectangles) with an aria-label for screen readers;
// swaps to a checkmark for ~1.5s after a successful copy. Falls back
// to a no-op if the Clipboard API is missing.
function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(value ?? ''));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch {
      // Clipboard API might be blocked — stay silent rather than
      // showing a misleading success.
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={label}
      title={label}
      className="inline-flex items-center justify-center rounded border border-border p-1 text-text-muted transition-colors hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1"
    >
      {copied ? (
        <svg
          viewBox="0 0 16 16"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 8.5l3 3 7-7" />
        </svg>
      ) : (
        <svg
          viewBox="0 0 16 16"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="4" y="4" width="9" height="10" rx="1" />
          <path d="M11 4V2.5A1.5 1.5 0 0 0 9.5 1H4.5A1.5 1.5 0 0 0 3 2.5v8A1.5 1.5 0 0 0 4.5 12H6" />
        </svg>
      )}
    </button>
  );
}

// Per-guest companions hint surfaced "by default" — without making the
// guest scroll past the artwork or click into the acompañantes input.
// Copy is conditional on the admin-entered `acompanantes_max` and on the
// guest's current RSVP state. Hidden entirely when there's nothing
// meaningful to say (individual entry / declined / already confirmed —
// the form-level "Ya confirmaste." handles the last case).
function CompanionsBanner({
  max,
  yaConfirmoAsistencia,
  yaRechazo,
}) {
  // Already declined: don't pester with companion info.
  if (yaRechazo) return null;

  // Individual entry: nothing to communicate.
  if (max < 1) return null;

  // Already responded: the form-level "Ya confirmaste." message handles
  // this case, so the banner stays silent to avoid duplicating copy.
  if (yaConfirmoAsistencia) return null;

  // Not yet responded — surface the allowance so the guest knows upfront.
  const copy =
    max === 1
      ? 'Admisión 1 persona'
      : `Admisión ${max} personas`;

  return (
    <p
      className="px-1 py-1 text-sm text-ink"
      data-testid="companions-banner"
    >
      {copy}
    </p>
  );
}
